import { Fragment, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { blockColor } from '../nbt/blockColor';
import { blockLabel } from '../nbt/blockLabel';
import {
  catalogItemToBlockName,
  inventoryLocation,
  TAB_ORDER,
  type InventoryCategory,
  type InventoryLayout,
  type InventoryLocation,
} from '../nbt/inventoryLayout';
import { loadEducationPack, prepareEducationAssets, type LoadedEducationPack } from '../textures/educationPack';
import { materialDataUrl } from '../textures/preview';

const TAB_ICONS: Record<InventoryCategory, string> = {
  construction: 'minecraft:bricks',
  equipment: 'minecraft:iron_sword',
  items: 'minecraft:red_bed',
  nature: 'minecraft:grass_block',
};

const TAB_LABELS: Record<InventoryCategory, string> = {
  construction: '構築',
  equipment: '所持品',
  items: 'アイテム',
  nature: '自然',
};

const CHEMISTRY_GROUPS = new Set([
  'itemGroup.name.element',
  'itemGroup.name.chemistrytable',
  'itemGroup.name.compounds',
  'itemGroup.name.products',
]);

const CHEMISTRY_NOTE = '化学のアイテムは、ワールドの せってい で「化学（かがく）」を オンにすると 出てきます';

type MapStep = 'tab' | 'tiles' | 'group' | 'found';

interface Availability {
  layout: InventoryLayout;
  location: InventoryLocation;
  pack: LoadedEducationPack;
  startTab: InventoryCategory;
}

const iconCache = new Map<string, string>();
const iconUnavailable = new Set<string>();

async function prepareIcons(ids: string[], pack: LoadedEducationPack): Promise<Map<string, string>> {
  const unique = [...new Set(ids)];
  const pending = unique.filter(id => !iconCache.has(id) && !iconUnavailable.has(id));
  if (pending.length) {
    const byId = new Map(pending.map(id => [id, catalogItemToBlockName(id)]));
    try {
      const assets = await prepareEducationAssets([], null, [...new Set(byId.values())]);
      for (const [id, name] of byId) {
        const url = materialDataUrl(name, assets);
        if (url) iconCache.set(id, url);
        else iconUnavailable.add(id);
      }
    } catch {
      for (const id of pending) iconUnavailable.add(id);
    }
  }
  const result = new Map<string, string>();
  for (const id of unique) {
    const url = iconCache.get(id);
    if (url) result.set(id, url);
  }
  return result;
}

function ItemIcon({ id, icons }: { id: string; icons: Map<string, string> }) {
  const source = icons.get(id);
  if (source) return <img src={source} alt="" />;
  return <span className="swatch" style={{ background: `#${blockColor(id).toString(16).padStart(6, '0')}` }} />;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted) { resolve(); return; }
    const onAbort = () => { window.clearTimeout(timer); resolve(); };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export function InventoryMapButton({ block }: { block: string }) {
  const [available, setAvailable] = useState<Availability | null>(null);
  const [open, setOpen] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [icons, setIcons] = useState(new Map<string, string>());
  const [step, setStep] = useState<MapStep>('tab');
  const [activeTab, setActiveTab] = useState<InventoryCategory>(TAB_ORDER[0]);
  const [childCount, setChildCount] = useState(0);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inventoryRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const handRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setAvailable(null);
    void loadEducationPack().then(pack => {
      if (cancelled) return;
      const layout = pack.registry.catalog.inventoryLayout;
      if (!layout) return;
      const location = inventoryLocation(layout, block);
      if (!location) return;
      const startTab = TAB_ORDER[(location.tabIndex + 2) % 4];
      setAvailable({ layout, location, pack, startTab });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [block]);

  useEffect(() => {
    if (!open || !available) return;
    let cancelled = false;
    const { layout, location, pack, startTab } = available;
    const ids = [
      ...TAB_ORDER.map(category => TAB_ICONS[category]),
      ...TAB_ORDER.filter(category => category === startTab || category === location.category)
        .flatMap(category => layout[category].map(tile => tile.icon)),
      ...(location.itemIndex !== null ? location.tile.items : []),
      location.item,
    ];
    void prepareIcons(ids, pack).then(loaded => {
      if (!cancelled) setIcons(previous => new Map([...previous, ...loaded]));
    });
    return () => { cancelled = true; };
  }, [open, available]);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, [open]);

  useEffect(() => {
    if (!open || !available) return;
    const { location, startTab } = available;
    const grouped = location.itemIndex !== null;
    const controller = new AbortController();
    const { signal } = controller;
    const rippleTimers = new Set<number>();

    const apply = (tab: InventoryCategory, nextStep: MapStep, children: number) => {
      flushSync(() => {
        setActiveTab(tab);
        setStep(nextStep);
        setChildCount(children);
      });
    };

    const resetHand = () => {
      const hand = handRef.current;
      if (hand) {
        hand.style.left = '50%';
        hand.style.top = '105%';
      }
    };

    const moveHand = async (element: Element | null) => {
      const inventory = inventoryRef.current;
      const hand = handRef.current;
      if (!inventory || !hand || !element) return sleep(800, signal);
      const frame = inventory.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      hand.style.left = `${rect.left - frame.left + rect.width / 2 - 14}px`;
      hand.style.top = `${rect.top - frame.top + rect.height / 2 - 6}px`;
      await sleep(800, signal);
    };

    const tap = async (element: Element | null) => {
      const inventory = inventoryRef.current;
      if (inventory && element) {
        const frame = inventory.getBoundingClientRect();
        const rect = element.getBoundingClientRect();
        const ripple = document.createElement('span');
        ripple.className = 'inventory-map-ripple';
        ripple.style.left = `${rect.left - frame.left + rect.width / 2}px`;
        ripple.style.top = `${rect.top - frame.top + rect.height / 2}px`;
        inventory.append(ripple);
        const timer = window.setTimeout(() => { ripple.remove(); rippleTimers.delete(timer); }, 600);
        rippleTimers.add(timer);
      }
      await sleep(350, signal);
    };

    const scrollToTile = async (element: Element | null) => {
      const grid = gridRef.current;
      if (!grid || !element) return sleep(600, signal);
      const frame = grid.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const top = grid.scrollTop + rect.top - frame.top - (grid.clientHeight - rect.height) / 2;
      if (Math.abs(top - grid.scrollTop) < 1) return;
      const settle = new Promise<void>(resolve => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          grid.removeEventListener('scrollend', finish);
          signal.removeEventListener('abort', finish);
          window.clearTimeout(timer);
          resolve();
        };
        const timer = window.setTimeout(finish, 900);
        grid.addEventListener('scrollend', finish, { once: true });
        signal.addEventListener('abort', finish, { once: true });
      });
      grid.scrollTo({ top, behavior: 'smooth' });
      await settle;
    };

    const groupTiles = grouped ? location.tile.items : [];

    if (reducedMotion) {
      const frame = window.requestAnimationFrame(() => {
        const grid = gridRef.current;
        if (!grid) return;
        const target = grouped
          ? [...grid.querySelectorAll<HTMLElement>('.inventory-map-tile.child')]
            .find(element => element.dataset.item === location.item) ?? null
          : grid.querySelector<HTMLElement>(`[data-tile="${location.tileIndex}"]`);
        if (!target) return;
        const gridFrame = grid.getBoundingClientRect();
        const rect = target.getBoundingClientRect();
        grid.scrollTop += rect.top - gridFrame.top - (grid.clientHeight - rect.height) / 2;
      });
      return () => {
        window.cancelAnimationFrame(frame);
        controller.abort();
        rippleTimers.forEach(timer => window.clearTimeout(timer));
      };
    }

    const run = async () => {
      while (!signal.aborted) {
        setActiveTab(startTab);
        setStep('tab');
        setChildCount(0);
        resetHand();
        await sleep(700, signal);
        if (signal.aborted) return;

        const tabElement = inventoryRef.current?.querySelector<HTMLElement>(`[data-tab="${location.category}"]`);
        await moveHand(tabElement ?? null);
        if (signal.aborted) return;
        await tap(tabElement ?? null);
        if (signal.aborted) return;
        apply(location.category, 'tiles', 0);
        await sleep(500, signal);
        if (signal.aborted) return;

        let target: Element | null = null;
        if (grouped) {
          const groupElement = gridRef.current?.querySelector<HTMLElement>(`[data-tile="${location.tileIndex}"]`) ?? null;
          await scrollToTile(groupElement);
          if (signal.aborted) return;
          await moveHand(groupElement);
          if (signal.aborted) return;
          await tap(groupElement);
          if (signal.aborted) return;
          flushSync(() => { setStep('group'); setChildCount(groupTiles.length); });
          await sleep(1600, signal);
          if (signal.aborted) return;
          target = [...(gridRef.current?.querySelectorAll<HTMLElement>('.inventory-map-tile.child') ?? [])]
            .find(element => element.dataset.item === location.item) ?? null;
        } else {
          target = gridRef.current?.querySelector<HTMLElement>(`[data-tile="${location.tileIndex}"]`) ?? null;
        }

        await scrollToTile(target);
        if (signal.aborted) return;
        await moveHand(target);
        if (signal.aborted) return;
        await tap(target);
        if (signal.aborted) return;
        flushSync(() => setStep('found'));
        await sleep(3000, signal);
      }
    };
    void run();

    return () => {
      controller.abort();
      rippleTimers.forEach(timer => window.clearTimeout(timer));
    };
  }, [open, available, reducedMotion]);

  if (!available) return null;

  const { layout, location, startTab } = available;
  const grouped = location.itemIndex !== null;
  const groupDelay = grouped ? Math.min(90, 1200 / location.tile.items.length) : 0;
  const steps = grouped ? [
    { icon: TAB_ICONS[location.category], kind: 'tab' },
    { icon: location.tile.icon, kind: 'group' },
    { icon: location.item, kind: 'item' },
  ] : [
    { icon: TAB_ICONS[location.category], kind: 'tab' },
    { icon: location.item, kind: 'item' },
  ];
  const litCount = step === 'tab' ? 0
    : step === 'tiles' ? 1
      : step === 'group' ? 2
        : steps.length;
  const showChemistryNote = location.tile.group !== undefined && CHEMISTRY_GROUPS.has(location.tile.group);

  const openDialog = () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setReducedMotion(reduced);
    setStep(reduced ? 'found' : 'tab');
    setActiveTab(reduced ? location.category : startTab);
    setChildCount(reduced && grouped ? location.tile.items.length : 0);
    setOpen(true);
  };

  const closeDialog = () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    setOpen(false);
  };

  const tiles = layout[activeTab];

  return (
    <>
      <button type="button" className="inventory-map-button" onClick={openDialog}>どこにある？</button>
      {open && (
        <dialog
          ref={dialogRef}
          className="inventory-map"
          data-step={step}
          onCancel={closeDialog}
          onClose={() => setOpen(false)}
          onClick={event => {
            if (event.target !== event.currentTarget) return;
            const rect = event.currentTarget.getBoundingClientRect();
            const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
            if (outside) closeDialog();
          }}
        >
          <div className="inventory-map-inner">
            <div className="inventory-map-head">
              <div className="inventory-map-target">
                <span className="inventory-map-target-icon"><ItemIcon id={location.item} icons={icons} /></span>
                <span>{blockLabel(block)}</span>
              </div>
              <button type="button" className="inventory-map-close" aria-label="閉じる" onClick={closeDialog}>×</button>
            </div>

            <div className="inventory-map-recipe" aria-hidden="true">
              {steps.map((part, index) => (
                <Fragment key={part.kind}>
                  {index > 0 && <span className="inventory-map-recipe-arrow">▶</span>}
                  <span className={`inventory-map-step${index < litCount ? ' lit' : ''}`}>
                    <span className="inventory-map-step-num">{index + 1}</span>
                    <ItemIcon id={part.icon} icons={icons} />
                  </span>
                </Fragment>
              ))}
            </div>

            <div className="inventory-map-inv" ref={inventoryRef}>
              <div className="inventory-map-tabs">
                {TAB_ORDER.map(category => (
                  <div
                    key={category}
                    className={`inventory-map-tab${category === activeTab ? ' on' : ''}`}
                    data-tab={category}
                  >
                    <ItemIcon id={TAB_ICONS[category]} icons={icons} />
                    <small>{TAB_LABELS[category]}</small>
                  </div>
                ))}
              </div>
              <div className="inventory-map-grid" ref={gridRef}>
                {tiles.map((tile, index) => {
                  const isTargetTile = !grouped && tile.items[0] === location.item;
                  const children = grouped && index === location.tileIndex ? tile.items.slice(0, childCount) : [];
                  return (
                    <Fragment key={`${tile.group ?? 'item'}-${index}`}>
                      <div
                        className={`inventory-map-tile${tile.group ? ' grp' : ''}${grouped && index === location.tileIndex && step !== 'tab' && step !== 'tiles' ? ' open' : ''}${step === 'found' && isTargetTile ? ' found' : ''}${step === 'found' && !isTargetTile ? ' dim' : ''}`}
                        data-tile={index}
                      >
                        <ItemIcon id={tile.icon} icons={icons} />
                      </div>
                      {children.map((item, childIndex) => {
                        const isTarget = step === 'found' && item === location.item;
                        return (
                          <div
                            key={`${item}-${childIndex}`}
                            className={`inventory-map-tile child${isTarget ? ' found' : ''}${step === 'found' && !isTarget ? ' dim' : ''}`}
                            data-item={item}
                            style={reducedMotion ? undefined : { animationDelay: `${childIndex * groupDelay}ms` }}
                          >
                            <ItemIcon id={item} icons={icons} />
                          </div>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </div>
              {!reducedMotion && (
                <div className="inventory-map-hand" ref={handRef} aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M9 11V4.5a1.5 1.5 0 0 1 3 0V10h.5V8.5a1.5 1.5 0 0 1 3 0V10h.5a1.5 1.5 0 0 1 3 0V11h.2a1.5 1.5 0 0 1 2.8.8V16c0 3.3-2.7 6-6 6h-2.3a6 6 0 0 1-4.6-2.2L4.3 15a1.6 1.6 0 0 1 2.4-2.1L9 15z" fill="#fff" stroke="#000" strokeWidth="1.2" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>

            {showChemistryNote && <p className="inventory-map-note">{CHEMISTRY_NOTE}</p>}
          </div>
        </dialog>
      )}
    </>
  );
}
